const SOURCE_CODES = {
  fleet: "1",
  hub: "2",
  legacy_app: "3",
} as const;

/**
 * The source system that is identifying this asset.
 *
 * - `fleet` - The fleet implementation identified this asset.
 *    For example, a3s-edge running on a commsboard in our (first-party) G2 fleet identified this from ALM's composition.json.
 *    For example, a3s-edge running on a commsboard in our G1 fleet identified this asset from (Vision Hub's) device.json.
 * - `hub` - Vision Hub identified this asset (as part of a hire).
 * - `legacy_app` - Legacy App (Vision App, Django, Two Bulls) identified this asset.
 */
export type AssetSource = keyof typeof SOURCE_CODES;

const REVERSE_SOURCE_CODES: Record<string, AssetSource> = {
  "1": "fleet",
  "2": "hub",
  "3": "legacy_app",
} as const;

const FLEET_CODES = {
  G1: "G1",
  G2: "G2",
} as const;

export type Fleet = keyof typeof FLEET_CODES;

const REVERSE_FLEET_CODES: Record<string, Fleet> = {
  G1: "G1",
  G2: "G2",
} as const;

const MAX_UNIQUE_LENGTH = 31;
const URL_SAFE_PATTERN = /^[a-zA-Z0-9_-]+$/;

declare const AssetIdBrand: unique symbol;
declare const AssetIdSourceBrand: unique symbol;

/**
 * A unique identifier for an A3S Asset.
 *
 * Structure:
 * - Source: the source system ('hub' or 'legacy_app')
 * - Fleet: the fleet identifier ('G1' or 'G2')
 * - Unique: a URL-safe string (1-31 chars, [a-zA-Z0-9_-])
 *
 * Can be parameterized by source for type safety:
 * - `AssetId` - any source (default)
 * - `AssetId<"hub">` - hub source only
 * - `AssetId<"legacy_app">` - legacy_app source only
 */
export type AssetId<S extends AssetSource = AssetSource> = string & {
  readonly [AssetIdBrand]: "AssetId";
  readonly [AssetIdSourceBrand]: S;
};

export class AssetIdError extends Error {
  constructor(
    public readonly kind: "OutOfRange" | "Format" | "InvalidSource" | "InvalidFleet",
    message: string,
  ) {
    super(message);
  }
}

/**
 * Constructs an AssetId from its component parts or throws an AssetIdError.
 *
 * @param source - The source system ('hub' or 'legacy_app')
 * @param fleet - The fleet identifier ('G1' or 'G2')
 * @param unique - URL-safe string (1-31 chars, [a-zA-Z0-9_-])
 */
const fromParts = <S extends AssetSource>(source: S, fleet: Fleet, unique: string): AssetId<S> => {
  if (!(source in SOURCE_CODES)) {
    throw new AssetIdError(
      "InvalidSource",
      `Source ${source} is not valid. Expected one of: ${Object.keys(SOURCE_CODES).join(", ")}.`,
    );
  }

  if (!(fleet in FLEET_CODES)) {
    throw new AssetIdError(
      "InvalidFleet",
      `Fleet ${fleet} is not valid. Expected one of: ${Object.keys(FLEET_CODES).join(", ")}.`,
    );
  }

  if (typeof unique !== "string" || unique.length === 0) {
    throw new AssetIdError("OutOfRange", `Unique string cannot be empty.`);
  }

  if (unique.length > MAX_UNIQUE_LENGTH) {
    throw new AssetIdError(
      "OutOfRange",
      `Unique string ${unique} exceeds maximum length. Expected at most ${String(MAX_UNIQUE_LENGTH)} characters, got ${String(unique.length)}.`,
    );
  }

  if (!URL_SAFE_PATTERN.test(unique)) {
    throw new AssetIdError(
      "Format",
      `Unique string ${unique} contains invalid characters. Expected only [a-zA-Z0-9_-].`,
    );
  }

  const sourceCode = SOURCE_CODES[source];
  return `A${sourceCode}-${fleet}-${unique}` as AssetId<S>;
};

/**
 * Canonical encoding is 'A' + source code + '-' + fleet + '-' + unique string.
 */
const toString = (id: AssetId): string => {
  return id as string;
};

/**
 * Parses an AssetId from canonical encoding or throws an AssetIdError.
 * (Accepts `/^A[23]-(G1|G2)-[a-zA-Z0-9_-]{1,31}$/`.)
 */
const fromString = (str: string): AssetId => {
  if (typeof str !== "string" || str.length < 7) {
    throw new AssetIdError(
      "Format",
      `String ${str} is not a valid AssetId. Expected format: A{source}-{fleet}-{unique}.`,
    );
  }

  if (str.charAt(0) !== "A") {
    throw new AssetIdError("Format", `String ${str} is not a valid AssetId. Expected to start with 'A'.`);
  }

  const sourceCode = str.charAt(1);
  if (!(sourceCode in REVERSE_SOURCE_CODES)) {
    throw new AssetIdError(
      "InvalidSource",
      `String ${str} has invalid source code '${sourceCode}'. Expected one of: ${Object.keys(REVERSE_SOURCE_CODES).join(", ")}.`,
    );
  }

  if (str.charAt(2) !== "-") {
    throw new AssetIdError("Format", `String ${str} is not a valid AssetId. Expected '-' separator at position 2.`);
  }

  const fleet = str.slice(3, 5);
  if (!(fleet in REVERSE_FLEET_CODES)) {
    throw new AssetIdError(
      "InvalidFleet",
      `String ${str} has invalid fleet '${fleet}'. Expected one of: ${Object.keys(REVERSE_FLEET_CODES).join(", ")}.`,
    );
  }

  if (str.charAt(5) !== "-") {
    throw new AssetIdError("Format", `String ${str} is not a valid AssetId. Expected '-' separator at position 5.`);
  }

  const unique = str.slice(6);

  if (unique.length === 0) {
    throw new AssetIdError("OutOfRange", `String ${str} has empty unique part.`);
  }

  if (unique.length > MAX_UNIQUE_LENGTH) {
    throw new AssetIdError(
      "OutOfRange",
      `String ${str} has unique part that exceeds maximum length. Expected at most ${String(MAX_UNIQUE_LENGTH)} characters, got ${String(unique.length)}.`,
    );
  }

  if (!URL_SAFE_PATTERN.test(unique)) {
    throw new AssetIdError(
      "Format",
      `String ${str} has unique part with invalid characters. Expected only [a-zA-Z0-9_-].`,
    );
  }

  return str as AssetId;
};

/**
 * Extracts the component parts from an AssetId.
 *
 * @param id - The AssetId to decompose
 * @returns An object with source, fleet, and unique properties
 */
const toParts = <S extends AssetSource>(id: AssetId<S>): { source: S; fleet: Fleet; unique: string } => {
  const str = id as string;
  const sourceCode = str.charAt(1);
  const source = REVERSE_SOURCE_CODES[sourceCode];
  const fleet = str.slice(3, 5) as Fleet;
  const unique = str.slice(6); // Skip 'A', source code, '-', fleet, and '-'

  if (!source) {
    throw new AssetIdError("InvalidSource", `AssetId ${str} has invalid source code '${sourceCode}'.`);
  }

  if (!(fleet in REVERSE_FLEET_CODES)) {
    throw new AssetIdError("InvalidFleet", `AssetId ${str} has invalid fleet '${fleet}'.`);
  }

  return { source, fleet, unique } as { source: S; fleet: Fleet; unique: string };
};

const source = (asset: AssetId) => toParts(asset).source;
const fleet = (asset: AssetId) => toParts(asset).fleet;
const unique = (asset: AssetId) => toParts(asset).unique;

/**
 * Type guard to check if an AssetId is from a specific source.
 *
 * @param id - The AssetId to check
 * @param source - The expected source
 * @returns True if the AssetId is from the specified source
 */
const isSource = <S extends AssetSource>(id: AssetId, source: S): id is AssetId<S> => {
  const { source: actualSource } = toParts(id);
  return actualSource === source;
};

/**
 * Template tag for interpolating AssetId values in template literals.
 * Example: f`Asset${assetId}Config` → "AssetA2-G1-my-assetConfig"
 */
const f = (strings: TemplateStringsArray, ...values: AssetId[]): string =>
  (strings[0] ?? "") + values.map((v, i) => toString(v) + (strings[i + 1] ?? "")).join("");

export const AssetId = {
  fromParts,
  toString,
  fromString,
  toParts,
  isSource,
  f,
  source,
  fleet,
  unique,
} as const;

export const ASSET_GENERATION_TO_FLEET = {
  G1: "G1",
  "G1.5": "G1",
  G2: "G2",
} as const satisfies Record<string, Fleet>;

export type AssetGeneration = keyof typeof ASSET_GENERATION_TO_FLEET;
